import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import {
  CALC_KEY_PREFIX,
  REPORT_CONFIG_VERSION,
  collectCalcRefKeys,
  isCustomGroup,
  listKpiTables,
  type CalculatedMetricDef,
  type CustomReportGroup,
  type MetricCatalogEntry,
  type ReportGroup,
} from "~/shared/kpi"
import {
  useSavedReportControllerCreateReportMutation,
  useSavedReportControllerUpdateReportMutation,
  useSavedReportControllerDeleteReportMutation,
} from "~/ui-rtk/api/savedReportApi"
import Input from "~/ui-rtk/components/ui/controls/Input"
import SortableGroupRow from "./SortableGroupRow"
import GroupEditorModal from "./GroupEditorModal"
import CalculatedMetricBuilderModal from "./CalculatedMetricBuilderModal"

interface ReportEditorModalProps {
  isOpen: boolean
  onClose: () => void
  /** Seed for the working copy — the groups of the currently active report. */
  initialGroups: ReportGroup[]
  initialName: string
  initialIsPrivate: boolean
  /** Report-scoped calculated metrics of the active report (seed for the working copy). */
  initialCalculatedMetrics: CalculatedMetricDef[]
  /** Company custom metrics offered in the picker + as calc operands. */
  customEntries?: MetricCatalogEntry[]
  /**
   * Synthesized entries for the company's CUSTOM_* initiative-type breakouts
   * (the `initiatives` source). Offered in the group picker and as calc
   * operands.
   */
  initiativeEntries?: MetricCatalogEntry[]
  /** True when a saved (non-default) report is active, enabling in-place Save + Delete. */
  canUpdate: boolean
  /** Id of the active saved report (only meaningful when `canUpdate`). */
  activeReportId: string
  /**
   * Resolves a builtin group's current metrics so it can be converted into an
   * editable custom group, mirroring the dashboard's own metric resolution.
   */
  resolveBuiltinGroupSeed: (slug: string) => {
    metricKeys: string[]
    showTotals: boolean
  }
  onSaved: (reportId: string) => void
  onDeleted: () => void
}

const SLUG_TITLES: Record<string, string> = Object.fromEntries(
  listKpiTables().map(table => [table.slug, table.title]),
)

function groupTitle(group: ReportGroup): string {
  if (isCustomGroup(group)) {
    return group.title
  }
  return SLUG_TITLES[group.slug] ?? group.slug
}

function newCustomGroup(): CustomReportGroup {
  return {
    kind: "custom",
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `group-${Date.now()}`,
    title: "New Group",
    metricKeys: [],
  }
}

// The report structure editor (the evolution of "Reorder Tables"): reorder,
// add, edit, and delete groups, then persist as a saved report. Editing the
// built-in Default report can only be saved as a new report; an active saved
// report can additionally be updated in place or deleted.
const ReportEditorModal: React.FC<ReportEditorModalProps> = ({
  isOpen,
  onClose,
  initialGroups,
  initialName,
  initialIsPrivate,
  initialCalculatedMetrics,
  customEntries,
  initiativeEntries,
  canUpdate,
  activeReportId,
  resolveBuiltinGroupSeed,
  onSaved,
  onDeleted,
}) => {
  const [groups, setGroups] = useState<ReportGroup[]>(initialGroups)
  const [name, setName] = useState(initialName)
  const [isPrivate, setIsPrivate] = useState(initialIsPrivate)
  const [calculatedMetrics, setCalculatedMetrics] = useState<
    CalculatedMetricDef[]
  >(initialCalculatedMetrics)
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
  // null = builder closed; { metric: null } = create; { metric } = edit.
  const [calcBuilder, setCalcBuilder] = useState<{
    metric: CalculatedMetricDef | null
  } | null>(null)

  const [createReport, { isLoading: isCreating }] =
    useSavedReportControllerCreateReportMutation()
  const [updateReport, { isLoading: isUpdating }] =
    useSavedReportControllerUpdateReportMutation()
  const [deleteReport, { isLoading: isDeleting }] =
    useSavedReportControllerDeleteReportMutation()
  const isSaving = isCreating || isUpdating || isDeleting

  // Reseed the working copy each time the editor is opened.
  useEffect(() => {
    if (isOpen) {
      setGroups(initialGroups)
      setName(initialName)
      setIsPrivate(initialIsPrivate)
      setCalculatedMetrics(initialCalculatedMetrics)
      setEditingGroupId(null)
      setCalcBuilder(null)
    }
  }, [
    isOpen,
    initialGroups,
    initialName,
    initialIsPrivate,
    initialCalculatedMetrics,
  ])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const groupIds = useMemo(() => groups.map(group => group.id), [groups])

  const editingGroup = useMemo(
    () =>
      groups.find(
        (group): group is CustomReportGroup =>
          group.id === editingGroupId && isCustomGroup(group),
      ) ?? null,
    [groups, editingGroupId],
  )

  const isDirty = useMemo(() => {
    const before = JSON.stringify({
      groups: initialGroups,
      name: initialName,
      isPrivate: initialIsPrivate,
      calculatedMetrics: initialCalculatedMetrics,
    })
    const after = JSON.stringify({ groups, name, isPrivate, calculatedMetrics })
    return before !== after
  }, [
    groups,
    name,
    isPrivate,
    calculatedMetrics,
    initialGroups,
    initialName,
    initialIsPrivate,
    initialCalculatedMetrics,
  ])

  const handleSaveCalcMetric = (metric: CalculatedMetricDef) => {
    setCalculatedMetrics(list => {
      const exists = list.some(item => item.id === metric.id)
      return exists
        ? list.map(item => (item.id === metric.id ? metric : item))
        : [...list, metric]
    })
    setCalcBuilder(null)
  }

  const handleRemoveCalcMetric = (id: string) => {
    // A metric another formula references can't be removed — its nested refs
    // would dangle and those formulas would go blank. Direct referencers are
    // sufficient as the guard (any transitive chain passes through one) and
    // keep the message actionable: every named formula actually contains the
    // metric as a removable operand.
    const calcKey = `${CALC_KEY_PREFIX}${id}`
    const dependents = calculatedMetrics.filter(
      item =>
        item.id !== id && collectCalcRefKeys(item.expression).includes(calcKey),
    )
    if (dependents.length > 0) {
      toast.error(
        `Used by ${dependents.map(item => `“${item.name}”`).join(", ")} — remove it from ${
          dependents.length === 1 ? "that formula" : "those formulas"
        } first.`,
      )
      return
    }
    setCalculatedMetrics(list => list.filter(item => item.id !== id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setGroups(items => {
        const oldIndex = items.findIndex(group => group.id === active.id)
        const newIndex = items.findIndex(group => group.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleAddGroup = () => {
    const group = newCustomGroup()
    setGroups(items => [...items, group])
    setEditingGroupId(group.id)
  }

  const handleRemoveGroup = (id: string) => {
    setGroups(items => items.filter(group => group.id !== id))
  }

  // Open the group editor. Custom groups edit in place; builtin groups are first
  // converted into an equivalent custom group seeded with the slug's current
  // metrics/title/totals, so the user can work off the existing template (and
  // persist the result only as a new/saved report, never the Default).
  const handleEditGroup = (group: ReportGroup) => {
    if (isCustomGroup(group)) {
      setEditingGroupId(group.id)
      return
    }
    const seed = resolveBuiltinGroupSeed(group.slug)
    // No `visualization` on the converted group: absent = 'table', matching
    // how the builtin group rendered (builtin groups never chart directly).
    const converted: CustomReportGroup = {
      kind: "custom",
      id: group.id,
      title: SLUG_TITLES[group.slug] ?? group.slug,
      metricKeys: seed.metricKeys,
      ...(seed.showTotals ? { showTotals: true } : {}),
    }
    setGroups(items =>
      items.map(item => (item.id === group.id ? converted : item)),
    )
    setEditingGroupId(group.id)
  }

  const handleGroupSaved = (updated: CustomReportGroup) => {
    setGroups(items =>
      items.map(group => (group.id === updated.id ? updated : group)),
    )
    setEditingGroupId(null)
  }

  const buildConfig = () => ({
    version: REPORT_CONFIG_VERSION,
    groups,
    ...(calculatedMetrics.length > 0 ? { calculatedMetrics } : {}),
  })

  const handleSaveAsNew = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error("Give the report a name before saving.")
      return
    }
    try {
      const created = await createReport({
        body: { name: trimmed, isPrivate, config: buildConfig() },
      }).unwrap()
      toast.success("Report created.")
      onSaved(created.id)
      onClose()
    } catch {
      toast.error("Failed to create report.")
    }
  }

  const handleUpdate = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error("Give the report a name before saving.")
      return
    }
    try {
      await updateReport({
        id: activeReportId,
        body: { name: trimmed, isPrivate, config: buildConfig() },
      }).unwrap()
      toast.success("Report saved.")
      onSaved(activeReportId)
      onClose()
    } catch {
      toast.error("Failed to save report.")
    }
  }

  const handleDelete = async () => {
    try {
      await deleteReport({ id: activeReportId }).unwrap()
      toast.success("Report deleted.")
      onDeleted()
      onClose()
    } catch {
      toast.error("Failed to delete report.")
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-system-blue-900 border border-system-blue-700 rounded-system-lg shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
          <div className="p-6 flex items-center justify-between border-b border-system-blue-700">
            <h2 className="text-xl font-semibold text-white">Edit Report</h2>
            <button
              onClick={onClose}
              className="text-system-blue-300 hover:text-white transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6 flex flex-col gap-4 overflow-hidden">
            <div className="flex items-end gap-4">
              <Input
                fullWidth
                label="Report name"
                value={name}
                onChange={event => setName(event.target.value)}
                placeholder="e.g. Weekly Exec Summary"
                classes={{ input: "text-white" }}
              />
              <label className="flex items-center gap-2 text-sm text-system-blue-200 cursor-pointer select-none whitespace-nowrap pb-2">
                <input
                  type="checkbox"
                  checked={isPrivate}
                  onChange={() => setIsPrivate(value => !value)}
                  className="checkbox checkbox-sm"
                />
                Private to me
              </label>
            </div>

            <div className="border border-system-blue-700 rounded-system-md p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-system-blue-200 text-sm">
                  Calculated metrics — build formulas from your metrics to reuse
                  in any group of this report.
                </p>
                <button
                  onClick={() => setCalcBuilder({ metric: null })}
                  className="px-3 py-1.5 text-xs text-white bg-system-blue-700 hover:bg-system-blue-600 border border-system-blue-600 rounded-system-md transition-colors whitespace-nowrap"
                >
                  + New calculated metric
                </button>
              </div>
              {calculatedMetrics.length === 0 ? (
                <p className="text-system-blue-300 text-xs">
                  None yet. Create one, then add it to a group like any other
                  metric.
                </p>
              ) : (
                <div className="space-y-1">
                  {calculatedMetrics.map(metric => (
                    <div
                      key={metric.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 bg-system-blue-800 border border-system-blue-600 rounded-system-md"
                    >
                      <span className="text-white text-sm truncate">
                        {metric.name}
                      </span>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <button
                          onClick={() => setCalcBuilder({ metric })}
                          className="text-xs text-system-blue-200 hover:text-white transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemoveCalcMetric(metric.id)}
                          className="text-xs text-system-blue-300 hover:text-system-error-400 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-system-blue-200 text-sm">
                Drag to reorder. Add custom groups of metrics, or remove groups
                you don&apos;t need.
              </p>
              <button
                onClick={handleAddGroup}
                className="px-3 py-1.5 text-xs text-white bg-system-blue-700 hover:bg-system-blue-600 border border-system-blue-600 rounded-system-md transition-colors whitespace-nowrap"
              >
                + Add Group
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 max-h-[24rem]">
              {groups.length === 0 ? (
                <p className="text-system-blue-300 text-sm">
                  No groups yet — add one to get started.
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={groupIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {groups.map(group => (
                        <SortableGroupRow
                          key={group.id}
                          id={group.id}
                          title={groupTitle(group)}
                          isBuiltin={!isCustomGroup(group)}
                          metricCount={
                            isCustomGroup(group)
                              ? group.metricKeys.length
                              : undefined
                          }
                          onEdit={() => handleEditGroup(group)}
                          onRemove={() => handleRemoveGroup(group.id)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>

          <div className="p-6 flex items-center justify-between border-t border-system-blue-700">
            <div>
              {canUpdate && (
                <button
                  onClick={handleDelete}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm text-system-error-400 hover:text-system-error-300 border border-system-error-700 hover:border-system-error-600 rounded-system-md transition-colors disabled:opacity-50"
                >
                  Delete Report
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-system-blue-300 hover:text-white border border-system-blue-600 hover:border-system-blue-500 rounded-system-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAsNew}
                disabled={isSaving || groups.length === 0}
                className="px-4 py-2 text-sm text-white border border-system-blue-500 hover:bg-system-blue-700 rounded-system-md transition-colors disabled:opacity-50"
              >
                Save as New
              </button>
              {canUpdate && (
                <button
                  onClick={handleUpdate}
                  disabled={isSaving || !isDirty || groups.length === 0}
                  className="px-4 py-2 text-sm text-white bg-system-brand-pink-500 hover:bg-system-brand-pink-600 rounded-system-md transition-colors disabled:opacity-50"
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {editingGroup && (
        <GroupEditorModal
          isOpen={editingGroupId !== null}
          group={editingGroup}
          calculatedMetrics={calculatedMetrics}
          customEntries={customEntries}
          initiativeEntries={initiativeEntries}
          onClose={() => setEditingGroupId(null)}
          onSave={handleGroupSaved}
        />
      )}

      {calcBuilder && (
        <CalculatedMetricBuilderModal
          isOpen={calcBuilder !== null}
          metric={calcBuilder.metric}
          customEntries={customEntries}
          initiativeEntries={initiativeEntries}
          calculatedMetrics={calculatedMetrics}
          onClose={() => setCalcBuilder(null)}
          onSave={handleSaveCalcMetric}
        />
      )}
    </>
  )
}

export default ReportEditorModal
